// ./src/utils/eventUpgrade.ts
//
// ROADMAP #209 — which upgrade of ONE event covers what it uses. An event can be bought UP a step later (VIP → Signature,
// paying the difference — owner, 2026-10-26), but the drawer still says, BEFORE the money, whether a plan covers
// everything the publish check refuses: the right one first is cheaper than two.
//
// The uses come from the server's own check (`eventPlanCheck`); each plan's values from ms_auth's catalog. A plan covers a
// use when it grants it the way the server's judge would for a bought plan (`boughtPlanGrants`: only what it states).
// Pure.
import type { EventPlanUse, EventUpgradeOption } from "@/utils/api";
import { FEATURE_CODES } from "@/config/featuresCode";
import { boughtPlanGrants, planFeatureMapOf } from "@/utils/planEntitlement";

/** Does this upgrade cover what the event uses and its plan does not include? `missing` = the plan features it leaves. */
export interface UpgradeCoverage {
  covers: boolean;
  missing: string[];
}

export const upgradeCoverage = (option: Pick<EventUpgradeOption, "planFeatures">, uses: readonly EventPlanUse[]): UpgradeCoverage => {
  const features = planFeatureMapOf(option.planFeatures ?? []);
  const missing = uses
    .filter((u) => !u.included)
    // A limit is a TOTAL the plan must allow: the write rule asks about one more, so ask with one fewer.
    .filter((u) => !boughtPlanGrants(features, u.featureCode, u.total != null ? Math.max(0, u.total - 1) : undefined))
    .map((u) => u.featureName);

  return { covers: missing.length === 0, missing };
};

/**
 * The upgrades in the order to offer them: the ones that cover everything first, cheapest first (the compromise the
 * catalog is built around — §7.6), then the rest by price. A price that cannot be read sorts last.
 */
export const orderUpgradeOptions = (options: readonly EventUpgradeOption[], uses: readonly EventPlanUse[]): EventUpgradeOption[] =>
  [...options].sort((a, b) => {
    const ca = upgradeCoverage(a, uses).covers ? 0 : 1;
    const cb = upgradeCoverage(b, uses).covers ? 0 : 1;

    if (ca !== cb) {
      return ca - cb;
    }

    return (a.amount ?? Number.POSITIVE_INFINITY) - (b.amount ?? Number.POSITIVE_INFINITY);
  });

/**
 * The words for one refused use: "Guest engagement (Party games, 3 polls)" or "Custom questions (7 — the plan allows 5)".
 * ⚠ ROADMAP #60 — the GUESTS ceiling is said in the server's own words, never its number: past the limit on a live event
 *   the total counts people the host is not shown (§7.4: never a locked count), and the server words it without one.
 */
export const planUseLabel = (u: EventPlanUse): string =>
  u.total != null && u.planLimit != null && u.featureCode !== FEATURE_CODES.MAX_GUESTS_QTY
    ? `${u.featureName} (${u.total} — the plan allows ${u.planLimit})`
    : `${u.featureName}${[...u.features, ...u.items].length ? ` (${[...u.features, ...u.items].join(", ")})` : ""}`;
